// ============================================================
// Autenticación real (R-PT-05): Authorization Code + PKCE contra
// el hosted UI de Cognito. Pórtico hace el login y DELEGA la
// identidad al kernel; no decide roles.
//
// Los tokens viven SOLO en memoria de módulo — nunca en
// localStorage/sessionStorage (una recarga de página implica
// volver a pasar por el hosted UI; si la cookie de sesión de
// Cognito sigue viva, el salto es transparente, sin contraseña).
// El único residuo persistente es el par {verifier,state} en
// sessionStorage mientras dura el redirect: es de un solo uso,
// no da acceso a nada por sí mismo y se borra al canjear el code.
// ============================================================

const DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN || "";
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || "";
// El redirect vuelve a la raíz de la SPA: única URL que no depende del
// routing de CloudFront y que está registrada en el app client (B3).
const REDIRECT_URI = `${window.location.origin}/`;
const PKCE_KEY = "portico.pkce";

let tokens = null; // { access_token, id_token, refresh_token, expires_at }
let refreshTimer = null;

const b64url = (buf) =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

async function s256(verifier) {
  return b64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
}

export function isConfigured() {
  return Boolean(DOMAIN && CLIENT_ID);
}

// Bearer para el kernel = ID TOKEN, no el access token: el pool usa
// username_attributes=["email"] (el username real es un UUID opaco), y el
// único token que lleva el claim `email` con el que R0 mapea usuario→rol
// (iam._identidades) es el ID token. auth.py del kernel valida ambos tipos.
export function getBearerToken() {
  if (tokens && Date.now() >= tokens.expires_at) tokens = null; // expirado sin refresco
  return tokens?.id_token || null;
}

// Paso 1 — saltar al hosted UI. El primer login de cada usuario fuerza el
// cambio de la contraseña temporal: lo gestiona Cognito, no la SPA (#243).
export async function login() {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(64)));
  const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state }));
  const q = new URLSearchParams({
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: "openid email profile",
    state,
    code_challenge_method: "S256",
    code_challenge: await s256(verifier),
  });
  window.location.assign(`${DOMAIN}/oauth2/authorize?${q}`);
}

// Paso 2 — al volver del redirect, canjear ?code por tokens (en memoria).
// Devuelve true si había code y se canjeó; false si no veníamos de un login.
export async function completeLoginFromUrl() {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  if (!code) return false;
  // El code es de un solo uso: fuera de la URL (y del historial) cuanto antes.
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  window.history.replaceState({}, "", url.pathname + url.search + url.hash);
  let saved = null;
  try { saved = JSON.parse(sessionStorage.getItem(PKCE_KEY) || "null"); } catch { /* corrupto -> null */ }
  sessionStorage.removeItem(PKCE_KEY);
  if (!saved?.verifier || saved.state !== state) {
    throw new Error("el estado del login no coincide — vuelve a entrar");
  }
  await exchange({
    grant_type: "authorization_code",
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier: saved.verifier,
  });
  return true;
}

async function exchange(params) {
  const r = await fetch(`${DOMAIN}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: CLIENT_ID, ...params }),
  });
  if (!r.ok) throw new Error(`intercambio de token fallido (${r.status})`);
  const t = await r.json();
  tokens = {
    access_token: t.access_token,
    id_token: t.id_token,
    // El grant refresh_token no devuelve otro refresh token: se conserva.
    refresh_token: t.refresh_token || tokens?.refresh_token || null,
    expires_at: Date.now() + t.expires_in * 1000,
  };
  scheduleRefresh(t.expires_in);
}

// Refresco silencioso (contrato §4: access 60 min, sesión absoluta 8 h):
// se renueva 5 min antes de expirar. Cuando Cognito deja de aceptar el
// refresh token (8 h), los tokens caen y el siguiente uso obliga a re-login.
function scheduleRefresh(expiresIn) {
  clearTimeout(refreshTimer);
  const delayMs = Math.max(expiresIn - 300, 60) * 1000;
  refreshTimer = setTimeout(async () => {
    try {
      await exchange({ grant_type: "refresh_token", refresh_token: tokens.refresh_token });
    } catch {
      tokens = null;
    }
  }, delayMs);
}

// Logout local: descartar los tokens en memoria y el refresco programado.
// (La cookie del hosted UI de Cognito sigue viva; cerrar esa sesión global
// sería /logout del hosted UI — fuera del alcance del MVP.)
export function clearTokens() {
  clearTimeout(refreshTimer);
  tokens = null;
}
